import { ActivityType, AssignmentCategory, Entity } from "@/enums";
import {
  AmiraResources,
  AssignedSkill,
  Assignment,
  LessonDetails,
  LessonResources,
  LessonSkillGroupMastery,
  parseMasteryPacingToClassName,
  Skill,
  SkillMasteryRecord,
  SkillOriginPriorityOrder,
  SkillStandardResponse,
  Student,
  StudentGroupMastery,
  SubgroupPriorityOrder,
} from "@/models";
import { getDomainFromMapping } from "./getDomainFromMapping";

const findAssignedSkills = (
  mainAssignment: Assignment,
  studentAssignments: Assignment[],
  skills: Skill[]
) => {
  const assignedSkills: {
    [key: string]: AssignedSkill;
  } = {};
  // similar logic to aggregateLessonsToStudentPlaylistEntries
  [mainAssignment, ...studentAssignments].forEach((assign) => {
    assign.manifest.forEach((m) => {
      if (m.skills?.length) {
        const foundAssignedSkill = assignedSkills[`${m.skills[0].skillId}`];
        if (foundAssignedSkill) {
          if (
            foundAssignedSkill.assignments.findIndex(
              (a) => a.assignmentId === assign.assignmentId
            ) === -1
          ) {
            foundAssignedSkill.assignments.push(assign);
          }
        } else {
          const skill = skills.find(
            (s) => m.skills && s.skillId === m.skills[0].skillId
          );
          if (skill) {
            assignedSkills[`${m.skills[0].skillId}`] = {
              skill: skill,
              assignments: [assign],
            };
          }
        }
      }
    });
  });
  return assignedSkills;
};

export const calcDisplayAssignmentsCategory = (assignedSkill: AssignedSkill) => {
  // find all possible categories
  const categories = assignedSkill.assignments.reduce((acc, cur) => {
    if (cur.assignmentCategory) {
      cur.assignmentCategory.forEach((cat) => {
        if (!acc.includes(cat)) {
          acc.push(cat);
        }
      });
    }
    return acc;
  }, [] as AssignmentCategory[]);
  // determine which display category by priority
  const origin = calcDisplayAssignmentCategory(categories);

  return origin;
};

export const calcDisplayAssignmentCategory = (categories: AssignmentCategory[]) => {
  return categories.reduce((acc, cur) => {
    const accIndex = SkillOriginPriorityOrder.findIndex((o) => o === acc);
    const curIndex = SkillOriginPriorityOrder.findIndex((o) => o === cur);
    if (accIndex !== -1 && curIndex !== -1 && curIndex < accIndex) {
      return cur;
    }
    return acc;
  }, AssignmentCategory.CORE);
}

const breakdownGroupsByMastery = (
  assignedSkill: AssignedSkill,
  students: Student[],
  masteryRecords: SkillMasteryRecord[]
) => {
  return students.reduce((acc, student) => {
    let domainName = parseMasteryPacingToClassName(null);
    const records = masteryRecords.find(
      (record) => record.studentId === student.id
    );
    if (records && records.records && records.records.length) {
      const skillRecord = records.records.find(
        (r) => r.skillId === assignedSkill.skill.skillId
      );
      if (skillRecord) {
        domainName = parseMasteryPacingToClassName(skillRecord.pacing);
      }
    }

    let assignment = assignedSkill.assignments.find(
      (a) => a.entityType === Entity.STUDENT && a.entityId === student.id
    );
    if (!assignment) {
      assignment = assignedSkill.assignments.find((a) =>
        [Entity.CLASS, Entity.SCHOOL, Entity.DISTRICT].includes(a.entityType)
      );
    }

    const foundGroup = acc.find((group) => group.groupName === domainName);
    if (foundGroup) {
      foundGroup.students.push({ student, assignment });
    } else {
      acc.push({
        groupName: domainName,
        timeToGrowth: 0,
        timeAssigned: 0,
        students: [{ student, assignment }],
        resources: 0,
        microlessons: 0,
        tutoring: 0,
        isMicrolessonsAverage: false,
        isResourceAverage: false,
        isTutoringAverage: false,
      });
    }

    return acc;
  }, [] as StudentGroupMastery[]);
};

export const countResourcesAssigned = (lessonResources: LessonResources[], skill: Skill, assignment?: Assignment) => {
  let studentTotalResources = 0;
  const foundResources = lessonResources.find(
    (r) => r.skillId === skill.skillId
  );
  // we choose to always add the class/district assignment resources into total
  if (foundResources) {
    studentTotalResources += foundResources.resources.length;
  }
  // then add in any additional teacher resources for this student
  if (assignment && assignment.entityType === Entity.STUDENT) {
    studentTotalResources = assignment.resources?.length || 0;
  }
  return studentTotalResources;
}

const aggregateGroupAssignedTypes = (
  studentGroups: StudentGroupMastery[],
  assignedSkill: AssignedSkill,
  lessonResources: LessonResources[],
  timeAssigned: { [key: string]: { [key: string]: { [key: string]: number } } },
  allResources: {skillId: string; resources: AmiraResources[]}[]
) => {
  return studentGroups.map((group) => {
    let {
      timeToGrowth, // TODO figure out where this comes from
      resources,
      microlessons,
      tutoring,
      students,
    } = group;
    const studentIds = students.map(s => s.student.id);

    let totalResources = 0;
    let totalMicrolessons = 0;
    let totalTutoring = 0;
    const skillResources = allResources.find(r => r.skillId === assignedSkill.skill.skillId)?.resources || [];
    const multiStoryResources = skillResources.filter(r => r.storyIds?.length && r.storyIds.length > 0);
   
    students.forEach((student, index) => {
      let studentTotalMicrolessons = 0;
      let studentTotalTutoring = 0;
      let studentTotalResources = 0;
      if (student.assignment) {
        const processedMultiStoryResources = new Set<string>();
        student.assignment.manifest
          .filter(
            (story) =>
              story.skills &&
              story.skills.length &&
              story.skills[0].skillId === assignedSkill.skill.skillId
          )
          .filter(story => {
            // filter out multi story resources that have already been processed
            const matchedMultiStoryResource = multiStoryResources.find(r => r.storyIds?.includes(story.storyId || ''));
            if (matchedMultiStoryResource && processedMultiStoryResources.has(matchedMultiStoryResource.resourceUniqueId || '')){
              return false;
            }
            if (matchedMultiStoryResource) {
              processedMultiStoryResources.add(matchedMultiStoryResource.resourceUniqueId || '');
            }
            return true;
          }).forEach((story) => {
            if (
              story.activityType === ActivityType.MICRO_LESSON ||
              story.activityType === ActivityType.ISTATION_LEGACY_ACTIVITY
            ) {
              studentTotalMicrolessons += 1;
            } else if (
              story.activityType === ActivityType.AMIRA_STORY ||
              story.activityType === ActivityType.TUTOR
            ) {
              studentTotalTutoring += 1;
            }
          });
      }
      // class or higher entity level assignment on this student use the lesson's default resources for this skill id
      studentTotalResources += countResourcesAssigned(lessonResources, assignedSkill.skill, student.assignment);
      totalMicrolessons += studentTotalMicrolessons;
      totalResources += studentTotalResources;
      totalTutoring += studentTotalTutoring;

      if (index === students.length - 1) {
        if (
          totalResources > 0 &&
          totalResources % students.length !== 0 
        ) {
          group.isResourceAverage = true;
        }
        if (
          totalMicrolessons > 0 &&
          totalMicrolessons % students.length !== 0
        ) {
          group.isMicrolessonsAverage = true;
        }
        if (
          totalTutoring > 0 &&
          totalTutoring % students.length !== 0
        ) {
          group.isTutoringAverage = true;
        }
      }
    });

    resources = Math.floor(totalResources / students.length);
    microlessons = Math.floor(totalMicrolessons / students.length);
    tutoring = Math.floor(totalTutoring / students.length);
    let ta = 0;
    const timeEntry = timeAssigned[assignedSkill.skill.skillId || '']?.[group.groupName || ''];
    if (timeEntry) {
      Object.keys(timeEntry).forEach(sId => {
        if (studentIds.includes(sId)) {
          ta += timeEntry[sId];
        }
      });
      if (studentIds.length > 0) {
        ta = Number((ta / studentIds.length).toFixed(1));
      }
    }

    return {
      ...group,
      timeToGrowth,
      resources,
      microlessons,
      tutoring,
      timeAssigned: ta,
    };
  });
};

const calcSkillGroups = (
  assignedSkills: {
    [key: string]: AssignedSkill;
  },
  lessonResources: LessonResources[],
  students: Student[],
  masteryRecords: SkillMasteryRecord[],
  timeAssigned: { [key: string]: { [key: string]: { [key: string]: number } } },
  allResources: {skillId: string; resources: AmiraResources[]}[],
  skillStandards?: SkillStandardResponse[]
) => {
  return Object.keys(assignedSkills).map((key) => {
    const assignedSkill = assignedSkills[key];

    const origin = calcDisplayAssignmentsCategory(assignedSkill);

    // initial breaking down each student into skill groups
    let studentGroups = breakdownGroupsByMastery(
      assignedSkill,
      students,
      masteryRecords || []
    );
    // aggregate total resource, microlesson, tutor assigned and time to growth and time assigned
    studentGroups = aggregateGroupAssignedTypes(
      studentGroups,
      assignedSkill,
      lessonResources,
      timeAssigned,
      allResources
    );

    // Sort student groups according to SubgroupPriorityOrder
    studentGroups.sort((a, b) => {
      const aIndex = SubgroupPriorityOrder.indexOf(a.groupName as any);
      const bIndex = SubgroupPriorityOrder.indexOf(b.groupName as any);

      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      return 0;
    });

    const standards = [] as string[];
    if (skillStandards && assignedSkill.skill.skillId) {
      const findSkillStandard = skillStandards.find(s => s.skill.skillId === assignedSkill.skill.skillId);
      if (findSkillStandard) {
        findSkillStandard.standards.forEach(element => {
          standards.push(element.standardId);
        });
      }
    }

    const skillGroup = {
      skill: assignedSkill.skill,
      domain: getDomainFromMapping(
        assignedSkill.skill.skillId?.split(".")[0] ?? ""
      ),
      origin, // 1 assignment category viewed by teacher
      standards,
      studentGroups,
    };
    return skillGroup;
  }) as LessonSkillGroupMastery[];
};

export const aggregateSkillGroups = (lesson: LessonDetails, allResources: {skillId: string; resources: AmiraResources[]}[]) => {
  const {
    mainAssignment,
    resources: lessonResources,
    students,
    masteryRecords,
    studentAssignments,
    skills,
    timeAssigned
  } = lesson;

  // determine all assigned skills in this lesson and match them to assignments
  const assignedSkills = findAssignedSkills(
    mainAssignment,
    studentAssignments,
    skills
  );

  return calcSkillGroups(
    assignedSkills,
    lessonResources,
    students,
    masteryRecords,
    timeAssigned,
    allResources,
    lesson.skillStandards   
  );
};
